import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  helperText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  parameterGrid: {
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    fontWeight: 600
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  }
}));

const getPlaceholderCount = text => {
  if (!text) {
    return 0;
  }

  const matches = [...text.matchAll(/{{\s*(\d+)\s*}}/g)];
  if (!matches.length) {
    return 0;
  }

  const indexes = matches
    .map(match => Number(match[1]))
    .filter(index => Number.isFinite(index));

  return indexes.length ? Math.max(...indexes) : 0;
};

const buildTemplateKey = template => `${template.name}::${template.language}`;

const getFileAccept = format => {
  switch (format) {
    case "IMAGE":
      return "image/*";
    case "VIDEO":
      return "video/*";
    case "DOCUMENT":
      return ".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,application/*,text/*";
    default:
      return "*";
  }
};

const getTemplateSchema = template => {
  const headerComponent = template?.components?.find(
    component => component.type === "HEADER"
  );
  const bodyComponent = template?.components?.find(
    component => component.type === "BODY"
  );
  const buttonsComponent = template?.components?.find(
    component => component.type === "BUTTONS"
  );

  const headerFormat = headerComponent?.format || null;
  const headerTextCount =
    headerFormat === "TEXT" ? getPlaceholderCount(headerComponent?.text) : 0;
  const headerRequiresMedia = ["IMAGE", "VIDEO", "DOCUMENT"].includes(
    headerFormat
  );
  const bodyTextCount = getPlaceholderCount(bodyComponent?.text);
  const buttons = (buttonsComponent?.buttons || []).map((button, index) => ({
    index,
    type: String(button.type || "").toLowerCase(),
    text: button.text || `Botao ${index + 1}`,
    url: button.url || "",
    requiresValue:
      String(button.type || "").toLowerCase() === "url"
        ? getPlaceholderCount(button.url) > 0
        : false
  }));

  const unsupportedReason = headerFormat &&
    headerFormat !== "TEXT" &&
    !headerRequiresMedia
      ? `Formato de header ainda nao suportado: ${headerFormat}.`
      : buttons.some(button => !["quick_reply", "url"].includes(button.type))
        ? "Este template usa um tipo de botao que ainda nao foi mapeado neste MVP."
        : null;

  return {
    headerFormat,
    headerTextCount,
    headerRequiresMedia,
    bodyTextCount,
    buttons,
    unsupportedReason
  };
};

const MetaCloudTemplateModal = ({ open, onClose, ticketId, onSent }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedKey, setSelectedKey] = useState("");
  const [bodyParameters, setBodyParameters] = useState([]);
  const [headerParameters, setHeaderParameters] = useState([]);
  const [buttonParameters, setButtonParameters] = useState([]);
  const [headerMedia, setHeaderMedia] = useState(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    let isMounted = true;

    const loadTemplates = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/messages/${ticketId}/meta-cloud/templates`
        );

        if (!isMounted) {
          return;
        }

        const list = data?.templates || [];
        setTemplates(list);

        if (list.length) {
          const fallbackTemplate = list[0];
          setSelectedKey(buildTemplateKey(fallbackTemplate));
        } else {
          setSelectedKey("");
        }
      } catch (err) {
        if (isMounted) {
          toastError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadTemplates();

    return () => {
      isMounted = false;
    };
  }, [open, ticketId]);

  const selectedTemplate = useMemo(
    () => templates.find(template => buildTemplateKey(template) === selectedKey),
    [selectedKey, templates]
  );

  const schema = useMemo(
    () => getTemplateSchema(selectedTemplate),
    [selectedTemplate]
  );

  useEffect(() => {
    if (!selectedTemplate) {
      setBodyParameters([]);
      setHeaderParameters([]);
      setButtonParameters([]);
      setHeaderMedia(null);
      return;
    }

    setBodyParameters(
      Array.from({ length: schema.bodyTextCount }).map(() => "")
    );
    setHeaderParameters(
      Array.from({ length: schema.headerTextCount }).map(() => "")
    );
    setButtonParameters(
      schema.buttons.map(button => ({
        index: button.index,
        type: button.type,
        value: button.type === "quick_reply" ? button.text : ""
      }))
    );
    setHeaderMedia(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [
    selectedTemplate,
    schema.bodyTextCount,
    schema.headerTextCount,
    schema.buttons
  ]);

  const handleArrayValueChange = (setter, index, value) => {
    setter(prevState =>
      prevState.map((item, itemIndex) => (itemIndex === index ? value : item))
    );
  };

  const handleButtonValueChange = (index, value) => {
    setButtonParameters(prevState =>
      prevState.map(button =>
        button.index === index ? { ...button, value } : button
      )
    );
  };

  const handleSubmit = async () => {
    if (!selectedTemplate) {
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("name", selectedTemplate.name);
      formData.append("language", selectedTemplate.language);
      formData.append("bodyParameters", JSON.stringify(bodyParameters));
      formData.append("headerParameters", JSON.stringify(headerParameters));
      formData.append("buttonParameters", JSON.stringify(buttonParameters));

      if (headerMedia) {
        formData.append("headerMedia", headerMedia);
      }

      await api.post(`/messages/${ticketId}/meta-cloud/template`, formData);

      toast.success("Template enviado com sucesso.");
      onClose();
      if (typeof onSent === "function") {
        onSent(selectedTemplate);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const renderTextInputs = (label, values, setter) =>
    values.map((value, index) => (
      <TextField
        key={`${label}-${index}`}
        fullWidth
        variant="outlined"
        margin="dense"
        label={`${label} ${index + 1}`}
        value={value}
        onChange={event =>
          handleArrayValueChange(setter, index, event.target.value)
        }
      />
    ));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enviar template oficial</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            <FormControl variant="outlined" fullWidth margin="dense">
              <InputLabel id="meta-cloud-template-label">Template</InputLabel>
              <Select
                labelId="meta-cloud-template-label"
                value={selectedKey}
                onChange={event => setSelectedKey(event.target.value)}
                label="Template"
              >
                {templates.map(template => (
                  <MenuItem
                    key={buildTemplateKey(template)}
                    value={buildTemplateKey(template)}
                  >
                    {template.name} ({template.language})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!templates.length && (
              <Typography className={classes.helperText}>
                Nenhum template aprovado foi encontrado nessa conexao oficial.
              </Typography>
            )}

            {selectedTemplate && (
              <>
                <Typography className={classes.helperText}>
                  Categoria: {selectedTemplate.category || "n/a"}
                </Typography>

                {schema.unsupportedReason ? (
                  <Typography className={classes.helperText}>
                    {schema.unsupportedReason}
                  </Typography>
                ) : (
                  <>
                    <div className={classes.parameterGrid}>
                      {renderTextInputs(
                        "Parametro do header",
                        headerParameters,
                        setHeaderParameters
                      )}
                      {renderTextInputs(
                        "Parametro do corpo",
                        bodyParameters,
                        setBodyParameters
                      )}
                    </div>

                    {schema.headerRequiresMedia && (
                      <>
                        <Typography className={classes.sectionTitle}>
                          Header de {schema.headerFormat.toLowerCase()}
                        </Typography>
                        <div className={classes.fileRow}>
                          <input
                            ref={fileInputRef}
                            type="file"
                            style={{ display: "none" }}
                            accept={getFileAccept(schema.headerFormat)}
                            onChange={event =>
                              setHeaderMedia(event.target.files?.[0] || null)
                            }
                          />
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<AttachFileIcon />}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            Selecionar arquivo
                          </Button>
                          <Typography className={classes.helperText}>
                            {headerMedia
                              ? headerMedia.name
                              : "Nenhum arquivo selecionado"}
                          </Typography>
                        </div>
                      </>
                    )}

                    {schema.buttons.length > 0 && (
                      <>
                        <Typography className={classes.sectionTitle}>
                          Botoes
                        </Typography>
                        <div className={classes.parameterGrid}>
                          {schema.buttons.map(button => (
                            <TextField
                              key={`button-${button.index}`}
                              fullWidth
                              variant="outlined"
                              margin="dense"
                              label={
                                button.type === "url"
                                  ? `${button.text} - sufixo da URL`
                                  : `${button.text} - payload`
                              }
                              value={
                                buttonParameters.find(
                                  item => item.index === button.index
                                )?.value || ""
                              }
                              onChange={event =>
                                handleButtonValueChange(
                                  button.index,
                                  event.target.value
                                )
                              }
                              required={button.requiresValue}
                              helperText={
                                button.type === "url"
                                  ? "Preencha apenas a parte variavel da URL."
                                  : "Se preferir, ajuste o payload tecnico do quick reply."
                              }
                            />
                          ))}
                        </div>
                      </>
                    )}

                    {!schema.headerRequiresMedia &&
                      !headerParameters.length &&
                      !bodyParameters.length &&
                      !schema.buttons.length && (
                        <Typography className={classes.helperText}>
                          Esse template nao exige parametros variaveis.
                        </Typography>
                      )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={
            loading ||
            sending ||
            !selectedTemplate ||
            Boolean(schema.unsupportedReason)
          }
        >
          {sending ? <CircularProgress size={20} color="inherit" /> : "Enviar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MetaCloudTemplateModal;
